import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/header.css';
import Logo from '../assets/logo.png';

const Header: React.FC = () => {
    const { t, i18n } = useTranslation();

    const [scrolled, setScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
    const headerRef = useRef<HTMLElement>(null);
    const location = useLocation();
    const isDesktop = () => window.innerWidth > 992;

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
                setActiveMenu(null);
                setIsLangMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setActiveMenu(null);
        setIsLangMenuOpen(false);
    }, [location]);

    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    }, [isMobileMenuOpen]);

    const toggleMobileMenu = () => {
        if (isMobileMenuOpen) {
            setActiveMenu(null);
        }

        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleSubMenu = (menuName: string) => {
        setActiveMenu(activeMenu === menuName ? null : menuName);
    };

    const openSubMenu = (menuName: string) => {
        if (isDesktop()) {
            setActiveMenu(menuName);
        }
    };

    const closeDesktopMenu = () => {
        if (isDesktop()) {
            setActiveMenu(null);
        }
    };

    const closeAll = () => {
        setActiveMenu(null);
        setIsMobileMenuOpen(false);
        setIsLangMenuOpen(false);
    };

    const changeLanguage = (lng: string) => {
        setIsLangMenuOpen(false);
        window.localStorage.setItem('app_language', lng);
        window.localStorage.setItem('app_language_initialized', 'true');

        void i18n.changeLanguage(lng).finally(() => {
            window.location.reload();
        });
    };

    const getCurrentLangLabel = () => {
        if (i18n.language.startsWith('en')) return 'English';
        if (i18n.language.startsWith('ja')) return '日本語';
        return '中文';
    };

    return (
        <>
            <div
                className={`menu-backdrop ${activeMenu && !isMobileMenuOpen ? 'active' : ''}`}
                onClick={() => setActiveMenu(null)}
            />

            <header
                ref={headerRef}
                className={`${scrolled ? 'scrolled' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}
                onMouseLeave={closeDesktopMenu}
            >
                <div className="nav-container">
                    <Link to="/" className="logo" onClick={closeAll}>
                        <img src={Logo} alt="Logo" style={{ borderRadius: '4px' }} />
                        {t('Header.logo')}
                    </Link>

                    <ul className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}>
                        <li className="mobile-lang-panel">
                            <div className="mobile-lang-title">{t('Header.language.label')}</div>
                            <div className="mobile-lang-options">
                                <button
                                    type="button"
                                    className={`mobile-lang-option${i18n.language.startsWith('zh') ? ' active' : ''}`}
                                    onClick={() => changeLanguage('zh')}
                                >
                                    中文
                                </button>
                                <button
                                    type="button"
                                    className={`mobile-lang-option${i18n.language.startsWith('en') ? ' active' : ''}`}
                                    onClick={() => changeLanguage('en')}
                                >
                                    English
                                </button>
                                <button
                                    type="button"
                                    className={`mobile-lang-option${i18n.language.startsWith('ja') ? ' active' : ''}`}
                                    onClick={() => changeLanguage('ja')}
                                >
                                    日本語
                                </button>
                            </div>
                        </li>

                        <li className="nav-item">
                            <Link to="/" className="nav-label" onClick={closeAll}>
                                {t('Header.nav.home')}
                            </Link>
                        </li>

                        <li className="nav-item" onMouseEnter={() => openSubMenu('services')}>
                            <div
                                className={`nav-label ${activeMenu === 'services' ? 'active' : ''}`}
                                onClick={() => toggleSubMenu('services')}
                            >
                                {t('Header.nav.services')} <span className="arrow">▼</span>
                            </div>
                            <div className={`mega-menu ${activeMenu === 'services' ? 'open' : ''}`}>
                                <div className="mega-content services-menu">
                                    <div className="services-menu-grid">
                                        <Link to="/services/china/enterprise/onestop" className="service-feature-card" onClick={closeAll}>
                                            <span className="service-feature-tag">{t('Header.services.tags.enterprise')}</span>
                                            <span className="service-feature-title">{t('Header.services.items.chinaEnt1')}</span>
                                        </Link>
                                        <Link to="/services/china/personal/digital" className="service-feature-card" onClick={closeAll}>
                                            <span className="service-feature-tag">{t('Header.services.tags.talent')}</span>
                                            <span className="service-feature-title">{t('Header.services.items.chinaPer1')}</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </li>

                        <li className="nav-item">
                            <Link to="/ai" className="nav-label" onClick={closeAll}>
                                {t('Header.nav.ai')}
                            </Link>
                        </li>

                        <li className="nav-item">
                            <Link to="/resources/news" className="nav-label" onClick={closeAll}>
                                {t('Header.nav.news')}
                            </Link>
                        </li>

                        <li className="nav-item">
                            <Link to="/resources" className="nav-label" onClick={closeAll}>
                                {t('Header.nav.resources')}
                            </Link>
                        </li>

                        <li className="nav-item">
                            <Link to="/about" className="nav-label" onClick={closeAll}>
                                {t('Header.nav.about')}
                            </Link>
                        </li>

                        <li className="nav-item" onMouseEnter={() => openSubMenu('contact')}>
                            <div
                                className={`nav-label ${activeMenu === 'contact' ? 'active' : ''}`}
                                onClick={() => toggleSubMenu('contact')}
                            >
                                {t('Header.nav.contact')} <span className="arrow">▼</span>
                            </div>
                            <div className={`mega-menu ${activeMenu === 'contact' ? 'open' : ''}`}>
                                <div className="mega-content services-menu">
                                    <div className="services-menu-grid">
                                        <Link to="/contact/projects" className="service-feature-card" onClick={closeAll}>
                                            <span className="service-feature-tag">{t('Header.contact.tags.project')}</span>
                                            <span className="service-feature-title">{t('Header.contact.items.project')}</span>
                                        </Link>
                                        <Link to="/contact/join" className="service-feature-card" onClick={closeAll}>
                                            <span className="service-feature-tag">{t('Header.contact.tags.join')}</span>
                                            <span className="service-feature-title">{t('Header.contact.items.joinUs')}</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>

                    <div className="nav-actions">
                        <div
                            className="lang-switch"
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                        >
                            {getCurrentLangLabel()} ▼
                            {isLangMenuOpen && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '120%',
                                        right: 0,
                                        background: 'var(--bg-color, #ffffff)',
                                        color: 'var(--text-color, #333333)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        minWidth: '100px',
                                        zIndex: 1000,
                                        border: '1px solid rgba(0,0,0,0.05)'
                                    }}
                                >
                                    <div className="lang-option" onClick={() => changeLanguage('zh')} style={{ padding: '10px 16px', fontSize: '14px' }}>中文</div>
                                    <div className="lang-option" onClick={() => changeLanguage('en')} style={{ padding: '10px 16px', fontSize: '14px' }}>English</div>
                                    <div className="lang-option" onClick={() => changeLanguage('ja')} style={{ padding: '10px 16px', fontSize: '14px' }}>日本語</div>
                                </div>
                            )}
                        </div>

                        <div
                            className={`mobile-menu-btn ${isMobileMenuOpen ? 'open' : ''}`}
                            onClick={toggleMobileMenu}
                        >
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
};

export default Header;
